import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import {
  Monitor, FileText, Receipt, FileCheck, ShoppingCart, Check, Plus,
  Shield, ChevronDown, ChevronUp, CreditCard, ArrowLeft,
  Cloud, Search, Wifi, CheckCircle2, Phone, Mail, X, Award, Clock,
  Lock, HelpCircle, MessageCircle, Headphones, AlertTriangle
} from 'lucide-react';
import { Button } from './ui/button';
import { useToast } from '../hooks/use-toast';
import MercadoPagoCheckoutModal from './MercadoPagoCheckoutModal';
import { Toaster } from './ui/toaster';
import CheckoutModal from './CheckoutModal';

const PRODUCTS = [
  {
    id: 'shoficina',
    name: 'SHOficina',
    description: 'Controle de OS, Estoque, Financeiro e Clientes. A base para sua oficina funcionar.',
    price: 268.90,
    badge: 'Mais vendido',
    icon: Monitor,
    color: 'from-blue-600 to-cyan-600',
    hasAddons: true,
  },
  {
    id: 'nfe',
    name: 'SHNF-e Emissor de NF-e (Mod. 55)',
    description: 'Emissor de Nota Fiscal modelo 55 (DANFE / A4). Obrigatória para revenda de peças e devoluções.',
    price: 409.90,
    badge: 'Exigido pela SEFAZ',
    icon: FileText,
    color: 'from-green-600 to-emerald-600',
  },
  {
    id: 'nfce',
    name: 'SHNFC-e Emissor de NFC-e (Mod. 65)',
    description: 'Cupom Fiscal Eletrônico para venda rápida de produtos direto no caixa. Substitui o bloco de papel.',
    price: 435.90,
    badge: 'Agilidade no Balcão',
    icon: Receipt,
    color: 'from-orange-600 to-amber-600',
  },
  {
    id: 'nfse',
    name: 'SHNFS-e Emissor de NFS-e',
    description: 'Nota Fiscal de Serviços eletrônica. Essencial para oficinas que cobram por serviços mecânicos.',
    price: 483.00,
    badge: 'Mais usado por oficinas',
    icon: FileCheck,
    color: 'from-purple-600 to-violet-600',
  }
];

const ADDONS = [
  { id: 'backup', name: 'Backup Online Plus', description: 'Proteja seus dados com backup na nuvem', price: 60.00, icon: Cloud },
  { id: 'placas20', name: '+20 Consultas de Placas Diárias', description: 'Aumente sua produtividade', price: 335.00, icon: Search },
  { id: 'placas50', name: '+50 Consultas de Placas Diárias', description: 'Alto volume de atendimentos', price: 685.00, icon: Search },
  { id: 'placas100', name: '+100 Consultas de Placas Diárias', description: 'Máxima capacidade para grandes operações', price: 1370.00, icon: Search },
  { id: 'rede2', name: 'Configuração de Rede em 2 PCs', description: 'Use em 2 computadores configurados pela equipe', price: 90.00, icon: Wifi },
  { id: 'rede3', name: 'Configuração de Rede em 3 PCs', description: 'Use em 3 computadores configurados pela equipe', price: 135.00, icon: Wifi },
];

const FAQS = [
  { q: 'Como funciona a licença anual?', a: 'A licença anual garante o uso completo do sistema por 12 meses, incluindo todas as atualizações de legislação fiscal e melhorias. Após esse período, é necessário renovar para continuar utilizando.' },
  { q: 'Posso testar antes de comprar?', a: 'Sim! Baixe gratuitamente no nosso site e teste todas as funcionalidades antes de adquirir a licença.' },
  { q: 'Existe limite de notas fiscais?', a: 'Não! Nossos módulos fiscais permitem emissão ilimitada de notas fiscais, desde que a licença esteja em dia.' },
  { q: 'Como recebo minha licença?', a: 'Após a confirmação do pagamento, você receberá automaticamente um email com os detalhes da compra e instruções para ativar sua licença.' },
  { q: 'Qual a forma de pagamento?', a: 'Aceitamos cartão de crédito via Stripe e PIX, cartão ou boleto via Mercado Pago. O processo é 100% seguro e criptografado.' },
  { q: 'Posso usar em mais de um computador?', a: 'Sim! O SHOficina funciona em rede local. Oferecemos pacotes de configuração remota para 2 ou 3 PCs.' },
];

const ComprarPage = () => {
  const [searchParams] = useSearchParams();
  const [cart, setCart] = useState([]);
  const [showCheckout, setShowCheckout] = useState(false);
  const [showMercadoPago, setShowMercadoPago] = useState(false);
  const [showAddons, setShowAddons] = useState(false);
  const [expandedFaq, setExpandedFaq] = useState(null);
  const [showExitModal, setShowExitModal] = useState(false);
  const [paymentVerified, setPaymentVerified] = useState(null); // 'paid' | 'failed' | null
  const exitShown = useRef(false);
  const { toast } = useToast();
  const paymentStatus = searchParams.get('payment');
  const sessionId = searchParams.get('session_id');

  const WHATSAPP_URL = 'https://wa.me/5516991378312?text=' + encodeURIComponent('Olá! Estava no site querendo comprar o SHOficina e fiquei com dúvidas. Pode me ajudar?');
  const API_URL = process.env.REACT_APP_BACKEND_URL || '';

  useEffect(() => { window.scrollTo(0, 0); }, []);

  // Verificar status do pagamento Stripe ao retornar
  useEffect(() => {
    if (!sessionId) return;
    let attempts = 0;
    const pollStatus = async () => {
      try {
        const res = await fetch(`${API_URL}/api/stripe/checkout-status/${sessionId}`);
        const data = await res.json();
        if (data.payment_status === 'paid') {
          setPaymentVerified('paid');
          if (typeof window.fbq === 'function') {
            window.fbq('track', 'Purchase', { currency: 'BRL', value: data.amount_total || 0 });
          }
          return;
        }
        attempts++;
        if (attempts < 8) setTimeout(pollStatus, 2000);
        else setPaymentVerified('failed');
      } catch { attempts++; if (attempts < 8) setTimeout(pollStatus, 2000); }
    };
    pollStatus();
  }, [sessionId, API_URL]);

  // Exit intent — desktop (mouse leaves viewport from top)
  useEffect(() => {
    const handleMouseLeave = (e) => {
      if (e.clientY <= 10 && cart.length > 0 && !exitShown.current && !paymentStatus) {
        exitShown.current = true;
        setShowExitModal(true);
      }
    };
    document.addEventListener('mouseleave', handleMouseLeave);
    return () => document.removeEventListener('mouseleave', handleMouseLeave);
  }, [cart, paymentStatus]);

  // Exit intent — mobile (tab switch / back button)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden' && cart.length > 0 && !exitShown.current && !paymentStatus) {
        exitShown.current = true;
        // Show on return
        const onReturn = () => {
          if (document.visibilityState === 'visible') {
            setShowExitModal(true);
            document.removeEventListener('visibilitychange', onReturn);
          }
        };
        document.addEventListener('visibilitychange', onReturn);
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [cart, paymentStatus]);

  const addToCart = (item) => {
    if (!cart.find(c => c.id === item.id)) {
      setCart(prev => [...prev, item]);
      toast({ title: "Adicionado ao carrinho", description: `${item.name} foi adicionado.` });
    }
  };
  const removeFromCart = (id) => setCart(prev => prev.filter(c => c.id !== id));
  const isInCart = (id) => cart.some(c => c.id === id);
  const getTotal = () => cart.reduce((sum, item) => sum + item.price, 0);

  return (
    <div className={`min-h-screen bg-slate-950 ${cart.length > 0 ? 'pb-20 lg:pb-0' : ''}`}>
      {/* Header */}
      <header className="sticky top-0 z-50 bg-slate-900/95 backdrop-blur-md border-b border-slate-800" data-testid="comprar-header">
        <div className="container mx-auto px-4 py-3 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <img src="./fernando-tech-logo.png" alt="Fernando Tech" className="h-10 w-10 rounded-lg" />
            <span className="text-white font-bold text-lg hidden sm:block">Fernando Tech</span>
          </Link>
          <div className="flex items-center gap-4">
            <a href="https://wa.me/5516991378312?text=Olá! Preciso de ajuda com a compra."
               target="_blank" rel="noopener noreferrer"
               className="hidden md:flex items-center gap-2 text-green-400 hover:text-green-300 text-sm font-medium">
              <Phone className="w-4 h-4" /> (16) 99137-8312
            </a>
            <Link to="/" className="flex items-center gap-2 text-slate-400 hover:text-white text-sm transition-colors" data-testid="voltar-site-btn">
              <ArrowLeft className="w-4 h-4" /> Voltar ao site
            </Link>
          </div>
        </div>
      </header>

      {/* Payment Status Banner */}
      {(sessionId || paymentStatus) && (
        <div className={`py-4 px-4 text-center ${
          paymentVerified === 'paid' || paymentStatus === 'success' ? 'bg-green-600'
          : paymentVerified === 'failed' || paymentStatus === 'failure' ? 'bg-red-600'
          : 'bg-yellow-600'
        }`} data-testid="payment-status-banner">
          <div className="container mx-auto flex items-center justify-center gap-2 text-white">
            {(paymentVerified === 'paid' || paymentStatus === 'success') && <><CheckCircle2 className="w-5 h-5" /><span className="font-semibold">Pagamento realizado com sucesso! Verifique seu email para receber a licença.</span></>}
            {paymentVerified === null && sessionId && <><Clock className="w-5 h-5" /><span className="font-semibold">Verificando pagamento...</span></>}
            {(paymentVerified === 'failed' || paymentStatus === 'failure') && <><X className="w-5 h-5" /><span className="font-semibold">O pagamento não foi concluído. Tente novamente.</span></>}
            {paymentStatus === 'pending' && <><Clock className="w-5 h-5" /><span className="font-semibold">Pagamento em processamento. Você receberá um email quando confirmado.</span></>}
          </div>
        </div>
      )}

      {/* Hero */}
      <section className="py-12 md:py-16 bg-gradient-to-b from-slate-900 to-slate-950 relative overflow-hidden">
        <div className="absolute inset-0 opacity-5">
          <div className="absolute top-10 left-10 w-64 h-64 bg-blue-500 rounded-full filter blur-3xl"></div>
          <div className="absolute bottom-10 right-10 w-80 h-80 bg-cyan-500 rounded-full filter blur-3xl"></div>
        </div>
        <div className="container mx-auto px-4 text-center relative z-10">
          <img src="./shoficina-logo.png" alt="SHOficina" className="h-14 mx-auto mb-4" />
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4">
            Gestão completa para oficinas que querem{' '}
            <span className="bg-gradient-to-r from-cyan-400 to-blue-400 bg-clip-text text-transparent">crescer com organização</span>
          </h1>
          <p className="text-base md:text-lg text-slate-300 max-w-2xl mx-auto mb-6">
            Selecione os módulos desejados para montar seu pacote personalizado.
          </p>
          <div className="flex flex-wrap justify-center gap-3 text-sm">
            {[
              { icon: Award, color: 'text-yellow-400', text: '+170.000 Licenças Vendidas' },
              { icon: Headphones, color: 'text-green-400', text: 'Suporte Humanizado' },
              { icon: FileText, color: 'text-blue-400', text: '+11 Milhões de NFs Emitidas' },
              { icon: Clock, color: 'text-cyan-400', text: 'Desde 1980 no mercado' },
            ].map((stat, i) => (
              <div key={i} className="flex items-center gap-2 bg-white/5 border border-white/10 px-4 py-2 rounded-full text-white">
                <stat.icon className={`w-4 h-4 ${stat.color}`} />{stat.text}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Subtitle */}
      <section id="secaocompra" className="pt-8 pb-2 bg-slate-950" style={{ scrollMarginTop: '80px' }}>
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-xl md:text-2xl font-bold text-white">Compre agora o que sua oficina precisa</h2>
          <p className="text-slate-400 mt-2 text-sm">Selecione os módulos desejados para adicioná-los ao carrinho de compras.</p>
        </div>
      </section>

      {/* Products + Cart */}
      <section className="py-8 bg-slate-950">
        <div className="container mx-auto px-4">
          <div className="flex flex-col lg:flex-row gap-8">
            {/* Products Column */}
            <div className="flex-1 space-y-5" data-testid="products-list">
              {PRODUCTS.map((product) => {
                const Icon = product.icon;
                const inCart = isInCart(product.id);
                return (
                  <div key={product.id}
                    className={`bg-slate-900 border rounded-2xl overflow-hidden transition-all duration-300 ${
                      inCart ? 'border-cyan-500 shadow-lg shadow-cyan-500/10' : 'border-slate-800 hover:border-slate-700'
                    }`}
                    data-testid={`product-card-${product.id}`}
                  >
                    <div className="p-5 md:p-6">
                      <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3 mb-3">
                        <div className="flex items-center gap-3">
                          <div className={`bg-gradient-to-r ${product.color} p-2.5 rounded-xl flex-shrink-0`}>
                            <Icon className="w-6 h-6 text-white" />
                          </div>
                          <div>
                            {product.badge && (
                              <span className="text-xs font-semibold text-yellow-400 bg-yellow-500/10 px-2 py-0.5 rounded-full">{product.badge}</span>
                            )}
                            <h3 className="text-lg font-bold text-white">{product.name}</h3>
                          </div>
                        </div>
                        <div className="text-left sm:text-right flex-shrink-0">
                          <span className="text-xs text-slate-500">Licença Anual</span>
                          <div className="text-2xl font-bold text-white">R$ {product.price.toFixed(2).replace('.', ',')}</div>
                        </div>
                      </div>
                      <p className="text-slate-400 text-sm mb-4">{product.description}</p>
                      <Button
                        onClick={() => inCart ? removeFromCart(product.id) : addToCart(product)}
                        className={`w-full sm:w-auto ${inCart ? 'bg-cyan-600 hover:bg-cyan-700 text-white' : 'bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-500 hover:to-blue-500 text-white'}`}
                        data-testid={`add-${product.id}-btn`}
                      >
                        {inCart ? <><Check className="w-4 h-4 mr-2" />Selecionado</> : <><Plus className="w-4 h-4 mr-2" />Adicionar</>}
                      </Button>
                    </div>

                    {/* Addons for SHOficina */}
                    {product.hasAddons && inCart && (
                      <div className="border-t border-slate-800">
                        <button onClick={() => setShowAddons(!showAddons)}
                          className="w-full px-5 md:px-6 py-3 flex items-center justify-between text-sm text-cyan-400 hover:bg-slate-800/50 transition-colors"
                          data-testid="toggle-addons-btn"
                        >
                          <span className="font-medium">Turbine seu pacote: Quero fazer mais com o SHOficina!</span>
                          {showAddons ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                        </button>
                        {showAddons && (
                          <div className="px-5 md:px-6 pb-4 space-y-2" data-testid="addons-list">
                            {ADDONS.map((addon) => {
                              const AddonIcon = addon.icon;
                              const addonInCart = isInCart(addon.id);
                              return (
                                <div key={addon.id}
                                  className={`flex items-center justify-between p-3 rounded-xl border transition-all ${
                                    addonInCart ? 'bg-cyan-500/5 border-cyan-500/30' : 'bg-slate-800/50 border-slate-700/50 hover:border-slate-600'
                                  }`}
                                >
                                  <div className="flex items-center gap-3 flex-1 min-w-0">
                                    <AddonIcon className="w-4 h-4 text-cyan-400 flex-shrink-0" />
                                    <div className="min-w-0">
                                      <span className="text-white text-sm font-medium block truncate">{addon.name}</span>
                                      <span className="text-slate-500 text-xs block truncate">{addon.description}</span>
                                    </div>
                                  </div>
                                  <div className="flex items-center gap-3 ml-3 flex-shrink-0">
                                    <span className="text-cyan-400 font-bold text-sm whitespace-nowrap">+ R$ {addon.price.toFixed(2).replace('.', ',')}</span>
                                    <button
                                      onClick={() => addonInCart ? removeFromCart(addon.id) : addToCart(addon)}
                                      className={`w-8 h-8 rounded-lg flex items-center justify-center transition-all ${
                                        addonInCart ? 'bg-cyan-600 text-white' : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                                      }`}
                                      data-testid={`addon-${addon.id}-btn`}
                                    >
                                      {addonInCart ? <Check className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
                                    </button>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Cart Sidebar */}
            <div className="hidden lg:block lg:w-96" data-testid="cart-sidebar">
              <div className="sticky top-20">
                <div className="bg-slate-900 border border-slate-800 rounded-2xl overflow-hidden">
                  <div className="bg-gradient-to-r from-cyan-600 to-blue-600 px-5 py-4">
                    <div className="flex items-center gap-2">
                      <ShoppingCart className="w-5 h-5 text-white" />
                      <h3 className="text-white font-bold text-lg">Seu Pacote</h3>
                    </div>
                  </div>
                  <div className="p-5">
                    {cart.length === 0 ? (
                      <div className="text-center py-8">
                        <ShoppingCart className="w-10 h-10 text-slate-700 mx-auto mb-3" />
                        <p className="text-slate-500 text-sm">Seu carrinho está vazio</p>
                        <p className="text-slate-600 text-xs mt-1">Escolha os módulos ao lado para montar seu pacote</p>
                      </div>
                    ) : (
                      <div className="space-y-2 mb-4">
                        {cart.map((item) => (
                          <div key={item.id} className="flex items-center justify-between py-2 border-b border-slate-800 last:border-0">
                            <p className="text-white text-sm font-medium truncate flex-1 min-w-0 mr-2">{item.name}</p>
                            <div className="flex items-center gap-2 flex-shrink-0">
                              <span className="text-cyan-400 font-bold text-sm">R$ {item.price.toFixed(2).replace('.', ',')}</span>
                              <button onClick={() => removeFromCart(item.id)} className="text-slate-500 hover:text-red-400 transition-colors p-0.5">
                                <X className="w-3.5 h-3.5" />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                    <div className="border-t border-slate-800 pt-4 mt-2">
                      <div className="flex items-center justify-between mb-4">
                        <span className="text-slate-400">Valor Total:</span>
                        <span className="text-2xl font-bold text-white">R$ {getTotal().toFixed(2).replace('.', ',')}</span>
                      </div>
                      <Button
                        onClick={() => cart.length > 0 && setShowCheckout(true)}
                        disabled={cart.length === 0}
                        className="w-full bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-500 hover:to-emerald-500 text-white font-bold py-5 text-base disabled:opacity-50 disabled:cursor-not-allowed"
                        data-testid="comprar-finalizar-btn"
                      >
                        <CreditCard className="w-5 h-5 mr-2" />
                        FINALIZAR COMPRA
                      </Button>

                      {/* Botão secundário Mercado Pago */}
                      <button
                        onClick={() => cart.length > 0 && setShowMercadoPago(true)}
                        disabled={cart.length === 0}
                        className="mt-2 w-full flex items-center justify-center gap-2 py-3 px-4 rounded-lg font-semibold text-sm border-2 border-sky-500/40 hover:border-sky-400 hover:bg-sky-500/10 text-sky-300 hover:text-sky-200 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                        style={{ background: 'rgba(0, 158, 227, 0.05)' }}
                        data-testid="comprar-mercadopago-btn"
                      >
                        <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor"><path d="M21 18v1a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v1h-9a2 2 0 0 0-2 2v8a2 2 0 0 0 2 2h9zm-9-2h10V8H12v8zm4-2.5a1.5 1.5 0 1 1 0-3 1.5 1.5 0 0 1 0 3z"/></svg>
                        Pagar com Mercado Pago
                        <span className="ml-1 text-[10px] uppercase tracking-wider px-1.5 py-0.5 bg-sky-500/20 border border-sky-400/30 rounded">PIX · Boleto</span>
                      </button>
                    </div>
                    <div className="mt-4 pt-4 border-t border-slate-800 space-y-2">
                      <div className="flex items-center gap-2 text-xs text-slate-400"><Lock className="w-3.5 h-3.5 text-green-400" /> Compra Segura e Criptografada</div>
                      <div className="flex items-center gap-2 text-xs text-slate-400"><Shield className="w-3.5 h-3.5 text-blue-400" /> Pagamento via Stripe ou Mercado Pago</div>
                      <div className="flex items-center gap-2 text-xs text-slate-400"><CheckCircle2 className="w-3.5 h-3.5 text-cyan-400" /> Licença enviada por email</div>
                      <div className="flex items-center gap-2 text-xs text-slate-400"><Headphones className="w-3.5 h-3.5 text-purple-400" /> Suporte humanizado incluso</div>
                    </div>
                  </div>
                </div>
                <a href="https://wa.me/5516991378312?text=Olá! Preciso de ajuda com a compra do SHOficina."
                   target="_blank" rel="noopener noreferrer"
                   className="flex items-center justify-center gap-2 mt-4 bg-green-600/10 border border-green-500/30 text-green-400 rounded-xl py-3 px-4 text-sm font-medium hover:bg-green-600/20 transition-colors"
                   data-testid="comprar-whatsapp-btn"
                >
                  <MessageCircle className="w-4 h-4" /> Precisa de ajuda? Fale conosco
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Section */}
      <section className="py-12 bg-slate-900/50 border-y border-slate-800">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
            {[
              { icon: Shield, color: 'bg-green-500/10 text-green-400', label: 'Sistema Seguro' },
              { icon: FileText, color: 'bg-blue-500/10 text-blue-400', label: 'Legislação Atualizada' },
              { icon: Monitor, color: 'bg-cyan-500/10 text-cyan-400', label: 'Teste Gratuito' },
              { icon: Headphones, color: 'bg-purple-500/10 text-purple-400', label: 'Suporte Humano' },
            ].map((badge, i) => (
              <div key={i} className="flex flex-col items-center gap-2">
                <div className={`p-3 rounded-full ${badge.color.split(' ')[0]}`}>
                  <badge.icon className={`w-6 h-6 ${badge.color.split(' ')[1]}`} />
                </div>
                <span className="text-white font-medium text-sm">{badge.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 bg-slate-950">
        <div className="container mx-auto px-4 max-w-3xl">
          <h2 className="text-xl md:text-2xl font-bold text-white text-center mb-8 flex items-center justify-center gap-2">
            <HelpCircle className="w-6 h-6 text-cyan-400" /> Perguntas Frequentes
          </h2>
          <div className="space-y-3">
            {FAQS.map((faq, i) => (
              <div key={i} className="bg-slate-900 border border-slate-800 rounded-xl overflow-hidden">
                <button onClick={() => setExpandedFaq(expandedFaq === i ? null : i)}
                  className="w-full px-6 py-4 flex items-center justify-between text-left hover:bg-slate-800/50 transition-colors"
                  data-testid={`faq-${i}-btn`}
                >
                  <span className="text-white font-medium text-sm">{faq.q}</span>
                  {expandedFaq === i ? <ChevronUp className="w-4 h-4 text-cyan-400 flex-shrink-0 ml-2" /> : <ChevronDown className="w-4 h-4 text-slate-500 flex-shrink-0 ml-2" />}
                </button>
                {expandedFaq === i && (
                  <div className="px-6 pb-4"><p className="text-slate-400 text-sm leading-relaxed">{faq.a}</p></div>
                )}
              </div>
            ))}
          </div>
          <div className="mt-8 text-center">
            <p className="text-slate-400 mb-3 text-sm">Ainda tem dúvidas?</p>
            <a href="https://wa.me/5516991378312?text=Tenho uma dúvida sobre o SHOficina."
               target="_blank" rel="noopener noreferrer"
               className="inline-flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white font-semibold px-6 py-3 rounded-xl transition-colors">
              <MessageCircle className="w-5 h-5" /> Falar com Especialista
            </a>
          </div>
        </div>
      </section>

      {/* 🇧🇷 Cobertura Nacional — incremento SEO (não polui visualmente, conteúdo enxuto) */}
      <section className="py-12 bg-slate-900/60 border-t border-slate-800">
        <div className="container mx-auto px-4 max-w-5xl">
          <div className="text-center mb-6">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-cyan-500/10 border border-cyan-500/30 rounded-full text-cyan-400 text-xs font-semibold mb-3">
              🇧🇷 Atendemos todo o Brasil
            </div>
            <h2 className="text-xl sm:text-2xl font-bold text-white mb-2">
              Implantação e suporte SHOficina nos 27 estados
            </h2>
            <p className="text-slate-400 text-sm max-w-2xl mx-auto">
              SHOficina, SHNF-e, SHNFC-e e SHNFS-e para <strong className="text-white">oficinas mecânicas, auto centers, retíficas, mecânicas diesel, oficinas de motos, caminhões e elétrica automotiva</strong> em todo o território nacional, com atendimento 100% remoto.
            </p>
          </div>

          {/* Grid compacto de estados — apenas siglas para SEO sem poluir */}
          <div className="grid grid-cols-6 sm:grid-cols-9 md:grid-cols-12 gap-1.5 mb-6">
            {[
              ['AC', 'Acre'], ['AL', 'Alagoas'], ['AP', 'Amapá'], ['AM', 'Amazonas'],
              ['BA', 'Bahia'], ['CE', 'Ceará'], ['DF', 'Distrito Federal'], ['ES', 'Espírito Santo'],
              ['GO', 'Goiás'], ['MA', 'Maranhão'], ['MT', 'Mato Grosso'], ['MS', 'Mato Grosso do Sul'],
              ['MG', 'Minas Gerais'], ['PA', 'Pará'], ['PB', 'Paraíba'], ['PR', 'Paraná'],
              ['PE', 'Pernambuco'], ['PI', 'Piauí'], ['RJ', 'Rio de Janeiro'], ['RN', 'Rio Grande do Norte'],
              ['RS', 'Rio Grande do Sul'], ['RO', 'Rondônia'], ['RR', 'Roraima'], ['SC', 'Santa Catarina'],
              ['SP', 'São Paulo'], ['SE', 'Sergipe'], ['TO', 'Tocantins'],
            ].map(([uf, nome]) => (
              <div
                key={uf}
                className="bg-slate-800/40 border border-slate-700/50 hover:border-cyan-500/40 rounded text-center py-1.5 transition-colors cursor-default"
                title={`Sistema para oficina mecânica em ${nome} — implantação e suporte remoto via Fernando Tech`}
              >
                <span className="text-cyan-400 font-bold text-xs">{uf}</span>
              </div>
            ))}
          </div>

          <p className="text-center text-slate-500 text-xs">
            Quer suporte remoto especializado? <Link to="/suporte-remoto-nacional" className="text-cyan-400 hover:text-cyan-300 underline">Conheça nosso atendimento nacional →</Link>
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 bg-slate-900 border-t border-slate-800">
        <div className="container mx-auto px-4 text-center">
          <div className="flex flex-wrap justify-center gap-6 text-sm text-slate-400 mb-4">
            <a href="tel:16991378312" className="flex items-center gap-2 hover:text-white transition-colors"><Phone className="w-4 h-4" /> (16) 99137-8312</a>
            <a href="mailto:contato@fernandotech.com.br" className="flex items-center gap-2 hover:text-white transition-colors"><Mail className="w-4 h-4" /> contato@fernandotech.com.br</a>
          </div>
          <p className="text-xs text-slate-600">Fernando Tech - Soluções em TI | Todos os direitos reservados</p>
        </div>
      </footer>

      {/* Mobile Cart Bottom Bar */}
      {cart.length > 0 && (
        <div className="fixed bottom-0 left-0 right-0 lg:hidden bg-slate-900/95 backdrop-blur-md border-t border-cyan-500/30 p-3 z-50 shadow-2xl" data-testid="mobile-cart-bar">
          <div className="flex items-center justify-between">
            <div>
              <span className="text-xs text-slate-400">{cart.length} {cart.length === 1 ? 'item' : 'itens'}</span>
              <div className="text-lg font-bold text-white">R$ {getTotal().toFixed(2).replace('.', ',')}</div>
            </div>
            <Button
              onClick={() => setShowCheckout(true)}
              className="bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-500 hover:to-emerald-500 text-white font-bold px-6"
              data-testid="mobile-checkout-btn"
            >
              <CreditCard className="w-4 h-4 mr-2" /> FINALIZAR
            </Button>
          </div>
        </div>
      )}

      {/* Checkout Modal */}
      <CheckoutModal
        isOpen={showCheckout}
        onClose={() => setShowCheckout(false)}
        cart={cart}
        total={getTotal()}
        onSuccess={() => { setCart([]); setShowCheckout(false); }}
      />

      {/* Mercado Pago Modal */}
      <MercadoPagoCheckoutModal
        isOpen={showMercadoPago}
        onClose={() => setShowMercadoPago(false)}
        cart={cart.map(c => ({ ...c, name: c.name, price: c.price, quantity: c.quantity || 1, id: c.id }))}
        total={getTotal()}
        onSuccess={() => { setCart([]); setShowMercadoPago(false); }}
      />

      {/* Cart Abandonment Exit Intent Modal */}
      {showExitModal && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center p-4"
          style={{ background: 'rgba(2,6,23,0.85)', backdropFilter: 'blur(6px)' }}
          data-testid="exit-intent-modal"
        >
          <div className="relative bg-slate-900 border border-slate-700 rounded-2xl shadow-2xl max-w-md w-full overflow-hidden">

            {/* Top accent bar */}
            <div className="h-1.5 w-full bg-gradient-to-r from-cyan-500 via-blue-500 to-cyan-400" />

            {/* Close button */}
            <button
              onClick={() => setShowExitModal(false)}
              className="absolute top-4 right-4 text-slate-400 hover:text-white transition-colors z-10"
              aria-label="Fechar"
              data-testid="exit-modal-close-btn"
            >
              <X className="w-5 h-5" />
            </button>

            <div className="p-6 pt-5">
              {/* Header */}
              <div className="flex items-start gap-3 mb-4">
                <div className="bg-yellow-500/10 p-2.5 rounded-xl flex-shrink-0 mt-0.5">
                  <AlertTriangle className="w-5 h-5 text-yellow-400" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-white leading-tight">
                    Espera! Não vá embora ainda...
                  </h2>
                  <p className="text-slate-400 text-sm mt-1">
                    Você deixou {cart.length} {cart.length === 1 ? 'item' : 'itens'} no carrinho.
                  </p>
                </div>
              </div>

              {/* Cart items */}
              <div className="bg-slate-800/60 border border-slate-700/50 rounded-xl p-4 mb-4 space-y-2">
                {cart.map(item => (
                  <div key={item.id} className="flex items-center justify-between text-sm">
                    <span className="text-slate-300 font-medium truncate pr-2">{item.name}</span>
                    <span className="text-cyan-400 font-bold whitespace-nowrap">
                      R$ {item.price.toFixed(2).replace('.', ',')}
                    </span>
                  </div>
                ))}
                <div className="border-t border-slate-700 pt-2 flex justify-between font-bold">
                  <span className="text-white">Total</span>
                  <span className="text-green-400 text-base">
                    R$ {getTotal().toFixed(2).replace('.', ',')}
                  </span>
                </div>
              </div>

              {/* Message */}
              <div className="bg-green-500/10 border border-green-500/20 rounded-xl p-4 mb-5">
                <p className="text-green-300 text-sm font-medium text-center leading-relaxed">
                  Ficou com dúvida? Fale com um especialista agora pelo WhatsApp e tire todas as suas dúvidas antes de comprar.
                </p>
              </div>

              {/* CTAs */}
              <div className="flex flex-col gap-3">
                <a
                  href={WHATSAPP_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => setShowExitModal(false)}
                  className="flex items-center justify-center gap-2 bg-green-600 hover:bg-green-500 text-white font-bold py-3.5 px-6 rounded-xl transition-all text-sm shadow-lg shadow-green-600/20"
                  data-testid="exit-modal-whatsapp-btn"
                >
                  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                  </svg>
                  Tirar Dúvidas no WhatsApp
                </a>

                <button
                  onClick={() => { setShowExitModal(false); setShowCheckout(true); }}
                  className="flex items-center justify-center gap-2 bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-500 hover:to-blue-500 text-white font-bold py-3.5 px-6 rounded-xl transition-all text-sm"
                  data-testid="exit-modal-checkout-btn"
                >
                  <CreditCard className="w-4 h-4" />
                  Finalizar Compra Agora
                </button>

                <button
                  onClick={() => setShowExitModal(false)}
                  className="text-slate-500 hover:text-slate-300 text-sm py-1 transition-colors"
                  data-testid="exit-modal-dismiss-btn"
                >
                  Não, obrigado. Vou sair mesmo.
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <Toaster />
    </div>
  );
};

export default ComprarPage;
